
import React, { useState } from "react";
import "./ResponseTabs.css";
import {
  ResponsiveContainer,
  BarChart as RBarChart,
  Bar,
  LineChart as RLineChart,
  Line,
  PieChart as RPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend
} from "recharts";



// Generate a unique color for each pie segment
function generateColor(index) {
  // Generate HSL color with varying hue
  const hue = (index * 137.508) % 360; // Golden angle for good distribution
  return `hsl(${hue}, 70%, 60%)`;
}

function ResponseTabs({ sqlQuery, tableData, graphData }) {
  const [activeTab, setActiveTab] = useState("table");
  React.useEffect(() => {
    // Scroll main page to the chat input position when answer changes
    if (window.chatInputRef && window.chatInputRef.current) {
      window.chatInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [sqlQuery, activeTab]);
  const answerTextRef = React.useRef(null);
  React.useEffect(() => {
    if (activeTab === "sql" && answerTextRef.current) {
      answerTextRef.current.scrollTop = 0;
    }
  }, [sqlQuery, activeTab]);
  const scrollContainerRef = React.useRef(null);
  React.useEffect(() => {
    if (activeTab === "table" && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [tableData, activeTab]);
  // Generate a unique color for each pie segment
  function generateColor(index) {
    // Generate HSL color with varying hue
    const hue = (index * 137.508) % 360; // Golden angle for good distribution
    return `hsl(${hue}, 70%, 60%)`;
  }
  const COLORS = Array.from({ length: 100 }, (_, i) => generateColor(i));



  const BAR_COLOR_LIST = [
    "#12CBC4", // pink
    "#006266", // teal
    "#B07AA1", // purple
    "#EDC948", // yellow
    "#4E79A7", // blue
    "#59A14F", // green
    "#F28E2B", // orange
    "#9C755F"  // brown
  ];


  // Prepare recharts data from graphData (visualizationData)
  let chartData = [];
  let xLabels = [];
  let yLabels = [];
  let xAxisKeys = [];
  let yAxisKey = "";
  if (graphData && graphData.axes && graphData.data) {
    xAxisKeys = (graphData.axes.x || []).map(x => x.id);
    yAxisKey = graphData.axes.y[0]?.id || "";
    yLabels = graphData.axes.y[0]?.labels || [];
    if (xAxisKeys.length === 0) {
      // No x axis: show a single bar/point/line with a default label
      xLabels = ["Value"];
      chartData = [
        yLabels.length > 0
          ? yLabels.reduce((obj, yLabel) => {
            const point = graphData.data.find(d => d[yAxisKey] === yLabel);
            obj.xLabel = "Value";
            obj[yLabel] = point ? point.value : 0;
            return obj;
          }, {})
          : (() => {
            // fallback: if yLabels is empty, try to use the first data point
            const obj = { xLabel: "Value" };
            if (graphData.data.length > 0 && graphData.data[0].value !== undefined) {
              obj.value = graphData.data[0].value;
            }
            return obj;
          })()
      ];
    } else {
      // Build combined x labels from all unique combinations in graphData.data
      xLabels = graphData.data.map(d => xAxisKeys.map(k => d[k]).join(' - '));
      xLabels = Array.from(new Set(xLabels));
      // Build chartData: each x label is a group, each y label is a bar
      chartData = xLabels.map(xLabel => {
        const obj = { xLabel };
        yLabels.forEach(yLabel => {
          // Find matching data point
          const point = graphData.data.find(d => xAxisKeys.map(k => d[k]).join(' - ') === xLabel && d[yAxisKey] === yLabel);
          obj[yLabel] = point ? point.value : 0;
        });
        return obj;
      });
    }
  }
  // For pie chart, use yLabels as series, xLabels as categories

  return (
    <>
      <div style={{ width: '100%', minWidth: '700px', marginBottom: 8 }}>
        <div style={{ fontSize: 13, color: '#555', fontWeight: 1000, marginBottom: 8, marginTop: 8 }}>
          The following visualizations display data in Mn($).
        </div>
      </div>
      <div className="response-tabs" style={{ width: "100%", minWidth: "700px" }}>
        <div className="tab-header" style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
          <button className={activeTab === "table" ? "active" : ""} onClick={() => setActiveTab("table")}>Table</button>
          <button className={activeTab === "bar" ? "active" : ""} onClick={() => setActiveTab("bar")}>Bar Chart</button>
          <button className={activeTab === "line" ? "active" : ""} onClick={() => setActiveTab("line")}>Line Chart</button>
          <button className={activeTab === "pie" ? "active" : ""} onClick={() => setActiveTab("pie")}>Pie Chart</button>
          {/* <button className={activeTab === "sql" ? "active" : ""} onClick={() => setActiveTab("sql")}>SQL Query</button> */}
        </div>

        <div className="tab-content" style={{ minHeight: 200 }}>
          {/*
          {activeTab === "sql" && (
            <div className="tab-panel" style={{ minHeight: 200 }}>
              <pre ref={answerTextRef} style={{ maxHeight: 200, overflowY: 'auto' }}>{sqlQuery}</pre>
            </div>
          )}
          */}

          {activeTab === "table" && tableData && tableData.headers && tableData.rows && tableData.rows.length > 0 && (
            <div className="tab-panel tab-panel-scroll" style={{ minHeight: 200 }} ref={scrollContainerRef}>
              <div className="tab-panel-scroll-inner">
                <table style={{ borderCollapse: "collapse", width: "auto" }}>
                  <colgroup>
                    {tableData.headers.map((_, idx) => (
                      <col key={idx} style={{ maxWidth: 50 }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {tableData.headers.map((h, idx) => (
                        <th key={idx} style={{ border: "1px solid #ccc", padding: "6px", background: "#f5f5f5" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let rows = tableData.rows;
                      // if (tableData.headers.length > 2) {
                      //   rows = [...rows].sort((a, b) => {
                      //     const valA = String(a[0] || "").toLowerCase();
                      //     const valB = String(b[0] || "").toLowerCase();
                      //     if (valA < valB) return -1;
                      //     if (valA > valB) return 1;
                      //     return 0;
                      //   });
                      // }
                      if (tableData.headers.length > 0) {
                        rows = [...rows].sort((a, b) => {
                          const colCount = tableData.headers.length;

                          // ✅ CASE 1: More than 2 columns → alphabetical sort by first column
                          if (colCount > 2) {
                            const valA = String(a[0] ?? "").toLowerCase();
                            const valB = String(b[0] ?? "").toLowerCase();

                            if (valA < valB) return -1;
                            if (valA > valB) return 1;
                            return 0;
                          }

                          // ✅ CASE 2: Exactly 2 columns → numeric sort by second column (DESC)
                          if (colCount === 2) {
                            const numA = Number(a[1]);
                            const numB = Number(b[1]);

                            // Handle non-numeric safely
                            if (!isNaN(numA) && !isNaN(numB)) {
                              return numB - numA; // higher number first
                            }
                            return 0;
                          }

                          // ✅ CASE 3: Only 1 column
                          if (colCount === 1) {
                            const valA = a[0];
                            const valB = b[0];

                            const numA = Number(valA);
                            const numB = Number(valB);

                            const isNumA = !isNaN(numA);
                            const isNumB = !isNaN(numB);

                            // If both are numbers → sort DESC
                            if (isNumA && isNumB) {
                              return numB - numA;
                            }

                            // Otherwise → text alphabetical sort
                            const strA = String(valA ?? "").toLowerCase();
                            const strB = String(valB ?? "").toLowerCase();

                            if (strA < strB) return -1;
                            if (strA > strB) return 1;
                            return 0;
                          }

                          return 0;
                        });
                      }
                      return rows.map((row, rIdx) => (
                        // <tr key={rIdx}>
                        //   {row.map((cell, cIdx) => (
                        //     <td
                        //       key={cIdx}
                        //       style={{
                        //         border: "1px solid #eee",
                        //         padding: "6px",
                        //         textAlign: cIdx === 0 ? "left" : "right",
                        //         fontWeight: cIdx === 0 ? "bold" : "normal"
                        //       }}
                        //     >
                        //       {cell}
                        //     </td>
                        //   ))}
                        // </tr>

                        <tr key={rIdx}>
                          {row.map((cell, cIdx) => {
                            // const isFirstCol = cIdx === 0;

                            // // Convert to number only for non-first columns
                            // const num = !isFirstCol ? Number(cell) : null;

                            // // Format: first column as-is, other columns to 2 decimals if numeric
                            // const displayValue =
                            //   isFirstCol
                            //     ? cell
                            //     : Number.isFinite(num)
                            //       ? num.toFixed(2)
                            //       : cell;

                            const num = Number(cell);
                            const displayValue =Number.isFinite(num)? num.toFixed(2): cell;

                            return (
                              <td
                                key={cIdx}
                                style={{
                                  border: "1px solid #eee",
                                  padding: "6px",
                                  textAlign: cIdx === 0 ? "left" : "right",
                                  fontWeight: cIdx === 0 ? "bold" : "normal"
                                }}
                              >
                                {displayValue}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()}
                    {/* Total row: only show if more than one row */}
                    {tableData.rows.length > 1 && tableData.headers.length > 1 && (
                      <tr>
                        <td style={{
                          border: "1px solid #eee",
                          padding: "6px",
                          textAlign: "left",
                          fontWeight: "bold",
                          background: "#f9f6e7"
                        }}>Total</td>
                        {tableData.headers.slice(1).map((_, cIdx, arr) => {
                          // Gather all numeric values in this column
                          const nums = tableData.rows
                            .map(row => {
                              const val = row[cIdx + 1];
                              const num = typeof val === 'number' ? val : parseFloat(val);
                              return isNaN(num) ? null : num;
                            })
                            .filter(v => v !== null);
                          const sum = nums.reduce((acc, n) => acc + n, 0);
                          return (
                            <td
                              key={cIdx + 1}
                              style={{
                                border: "1px solid #eee",
                                padding: "6px",
                                textAlign: "right",
                                fontWeight: "bold",
                                background: "#f9f6e7"
                              }}
                            >
                              {nums.length > 0 ? sum.toFixed(2) : ""}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "bar" && (
            <div className="tab-panel" style={{ minHeight: 200, display: 'flex', alignItems: 'flex-start' }}>
              {chartData.length > 0 && (
                <ResponsiveContainer
                  width={chartData.length <= 3 ? chartData.length * 120 : '100%'}
                  height={340}
                >
                  <RBarChart data={chartData} margin={{ left: 0, right: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="xLabel"
                      interval={0}
                      minTickGap={0}
                      tick={({ x, y, payload }) => {
                        const [quarter, week] = (payload.value || '').split(' - ');
                        const truncate = str => (typeof str === 'string' ? (str.length > 8 ? str.slice(0, 8) + '…' : str) : '');
                        const fullLabel = payload.value || '';
                        return (
                          <g transform={`translate(${x},${y + 10})`}>
                            <title>{fullLabel}</title>
                            <text x={0} y={0} textAnchor="middle" fontSize={12} fill="#666">
                              <tspan x={0} dy={0}>{truncate(quarter)}</tspan>
                              <tspan x={0} dy={14}>{truncate(week)}</tspan>
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis />
                    <ChartTooltip />
                    <Legend contentStyle={{ marginTop: 32 }} />
                    {yLabels.map((key, index) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        fill={BAR_COLOR_LIST[index % BAR_COLOR_LIST.length]}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </RBarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          {activeTab === "line" && (
            <div className="tab-panel" style={{ minHeight: 200 }}>
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height={340}>
                  <RLineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="xLabel"
                      interval={0}
                      minTickGap={0}
                      tick={({ x, y, payload }) => {
                        const [quarter, week] = (payload.value || '').split(' - ');
                        const truncate = str => (typeof str === 'string' ? (str.length > 5 ? str.slice(0, 5) + '…' : str) : '');
                        const fullLabel = payload.value || '';
                        return (
                          <g transform={`translate(${x},${y + 10})`}>
                            <title>{fullLabel}</title>
                            <text x={0} y={0} textAnchor="middle" fontSize={12} fill="#666">
                              <tspan x={0} dy={0}>{truncate(quarter)}</tspan>
                              <tspan x={0} dy={14}>{truncate(week)}</tspan>
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis />
                    <ChartTooltip />
                    <Legend contentStyle={{ marginTop: 32 }} />
                    {yLabels.length === 0 && chartData[0] && chartData[0].value !== undefined && (
                      <Line type="monotone" dataKey="value" stroke={COLORS[0]} />
                    )}
                    {yLabels.length > 0 && yLabels.map((h, idx) => (
                      <Line key={h} type="monotone" dataKey={h} stroke={COLORS[idx % COLORS.length]} />
                    ))}
                  </RLineChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          {activeTab === "pie" && (
            <div className="tab-panel" style={{ minHeight: 200 }}>
              {chartData.length > 0 && yLabels.length > 0 && (
                <PieSelector chartData={chartData} headers={["xLabel", ...yLabels]} COLORS={COLORS} />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}  // <-- CLOSE ResponseTabs here

// PieSelector component for pie chart column selection
function PieSelector({ chartData, headers, COLORS }) {
  const yHeaders = headers.slice(1);
  return (
    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '32px', justifyContent: 'center', marginTop: 32, marginBottom: 32, overflowX: 'auto', paddingLeft: 24, paddingRight: 24 }}>
      {yHeaders.map((yHeader, idx) => {
        const pieData = chartData.map((row, i) => ({ name: row[headers[0]], value: row[yHeader], color: COLORS[i] || generateColor(i) }));
        return (
          <div key={yHeader} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 360, marginBottom: 32, paddingLeft: 24, paddingRight: 24 }}>
            <ResponsiveContainer width={280} height={280}>
              <RPieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  // label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  // label={({ name, value, percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip />
              </RPieChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 16, fontWeight: '500', fontSize: 16 }}>{yHeader}</div>
            <div style={{ marginTop: 12, marginBottom: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, width: '100%', maxWidth: 180 }}>
              {pieData.map((entry, i) => (
                <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 16, height: 16, background: entry.color, borderRadius: 4, marginRight: 4, flexShrink: 0 }}></span>
                  <span style={{ fontSize: 14, minWidth: 120, display: 'inline-block', whiteSpace: 'nowrap' }}>{entry.name}: <span style={{ fontWeight: '500' }}>{entry.value}</span></span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ResponseTabs;